// Cookie/analytics consent state.
//
// Stored on the device rather than the server: the decision has to be readable
// before we know who the visitor is, and guests check out without an account.
//
// Only two things are gated by this — Google Tag Manager (src/../public/gtm.js)
// and the persistent analytics session id (src/utils/analytics.js). Strictly
// necessary storage (auth session, the analytics session id's replacement) is
// not gated and must not be: consent does not apply to it.

const STORAGE_KEY = 'rs_cookie_consent';

/**
 * Bump to re-ask everyone — e.g. when a new processor starts receiving data.
 * 2 (2026-10-28): Google Ads conversion measurement, remarketing and hashed
 * email/phone for enhanced conversions. Earlier acceptances only covered GA4.
 */
export const CONSENT_VERSION = 2;

export const CONSENT_GRANTED = 'granted';
export const CONSENT_DENIED = 'denied';

/** Fired on window whenever the stored decision changes. */
export const CONSENT_CHANGED_EVENT = 'rs:consent-changed';
/** Fired on window to re-open the banner (the footer's "Cookie settings"). */
export const CONSENT_REOPEN_EVENT = 'rs:consent-reopen';

/**
 * The stored decision, or null when there is none to honour.
 *
 * Returns null rather than throwing on anything unexpected — corrupt JSON, a
 * stale version, an unknown status, or storage being unavailable in private
 * mode. Every one of those means "we do not have a decision we can rely on",
 * and the caller's job is then to ask again.
 */
export function getConsent() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (parsed.status !== CONSENT_GRANTED && parsed.status !== CONSENT_DENIED) return null;

    return parsed;
  } catch {
    return null;
  }
}

/** Record a decision. Storage failures are non-fatal — we simply ask again next visit. */
export function setConsent(status) {
  if (status !== CONSENT_GRANTED && status !== CONSENT_DENIED) {
    throw new Error(`Unknown consent status: ${status}`);
  }

  const record = { status, ts: new Date().toISOString(), version: CONSENT_VERSION };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Private mode, quota, storage disabled. The choice still applies to this
    // page view via the event below; it just will not survive a reload.
  }

  try {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: record }));
  } catch {
    // CustomEvent unavailable (very old browser) — nothing else to do.
  }

  return record;
}

/** True when the visitor has actively agreed. Absence of a decision is not agreement. */
export function hasAnalyticsConsent() {
  return getConsent()?.status === CONSENT_GRANTED;
}

/** True when there is no decision on record at the current version. */
export function needsDecision() {
  return getConsent() === null;
}

/** Ask again — used by the footer's "Cookie settings" control. */
export function reopenConsent() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignored for the same reason as setConsent.
  }

  try {
    window.dispatchEvent(new CustomEvent(CONSENT_REOPEN_EVENT));
  } catch {
    // Ignored.
  }
}
